import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { QrCode, Package, Eye, StopCircle, CheckCircle, ArrowLeft, Thermometer, MapPin, Shield, Loader2, Camera, CameraOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Action Selection Step Component
const ActionStep = ({ onSelectAction }) => (
  <div className="space-y-4">
    <h2 className="text-xl font-bold text-center text-slate-900 mb-6">Select Action</h2>
    
    <button
      onClick={() => onSelectAction('new')}
      className="w-full p-6 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl text-white flex items-center gap-4 hover:shadow-lg transition-all active:scale-[0.98] min-h-[80px]"
    >
      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
        <Package className="w-7 h-7" />
      </div>
      <div className="text-left">
        <div className="font-bold text-lg">New Shipment</div>
        <div className="text-sm text-white/80">Start tracking a new pallet</div>
      </div>
    </button>

    <button
      onClick={() => onSelectAction('status')}
      className="w-full p-6 bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl text-white flex items-center gap-4 hover:shadow-lg transition-all active:scale-[0.98] min-h-[80px]"
    >
      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
        <Eye className="w-7 h-7" />
      </div>
      <div className="text-left">
        <div className="font-bold text-lg">View Status</div>
        <div className="text-sm text-white/80">Check shipment progress</div>
      </div>
    </button>

    <button
      onClick={() => onSelectAction('end')}
      className="w-full p-6 bg-gradient-to-r from-orange-600 to-red-500 rounded-2xl text-white flex items-center gap-4 hover:shadow-lg transition-all active:scale-[0.98] min-h-[80px]"
    >
      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
        <StopCircle className="w-7 h-7" />
      </div>
      <div className="text-left">
        <div className="font-bold text-lg">End Shipment</div>
        <div className="text-sm text-white/80">Complete delivery tracking</div>
      </div>
    </button>
  </div>
);

// Scanning Step Component with Real Camera
const ScanningStep = ({ onBack, onScanComplete, selectedAction }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const startScanner = async () => {
    try {
      setCameraError(null);
      setIsScanning(true);
      
      html5QrCodeRef.current = new Html5Qrcode('qr-reader');
      
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          // QR code scanned successfully
          handleScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Scan error - ignore, keep scanning
        }
      );
    } catch (err) {
      setCameraError(err.message || 'Unable to access camera');
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      await html5QrCodeRef.current.stop();
    }
    setIsScanning(false);
  };

  const handleScanSuccess = (decodedText) => {
    stopScanner();
    
    // Parse SSCC from QR code (could be raw SSCC or URL containing SSCC)
    let sscc = decodedText;
    if (decodedText.includes('sscc=')) {
      sscc = decodedText.split('sscc=')[1].split('&')[0];
    }
    
    const scannedData = {
      sscc: sscc || `00${Math.floor(Math.random() * 10000000000000000).toString().padStart(16, '0')}`,
      pallet_id: `PLT${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      device_id: `PALLET_${Math.floor(Math.random() * 100).toString().padStart(3, '0')}`,
    };
    
    toast.success('QR Code scanned successfully!');
    onScanComplete(scannedData);
  };

  const handleManualSubmit = () => {
    if (!manualInput.trim()) {
      toast.error('Please enter an SSCC');
      return;
    }
    
    const scannedData = {
      sscc: manualInput.trim(),
      pallet_id: `PLT${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      device_id: `PALLET_${Math.floor(Math.random() * 100).toString().padStart(3, '0')}`,
    };
    
    onScanComplete(scannedData);
  };

  const handleSimulateScan = () => {
    const scannedData = {
      sscc: `007071456${Math.floor(Math.random() * 100000000).toString().padStart(9, '0')}`,
      pallet_id: `PLT${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      device_id: `PALLET_${Math.floor(Math.random() * 100).toString().padStart(3, '0')}`,
    };
    
    toast.success('Demo scan completed!');
    onScanComplete(scannedData);
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  return (
    <div className="space-y-5 text-center">
      <button
        onClick={() => { stopScanner(); onBack(); }}
        className="absolute top-4 left-4 p-2 text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-6 h-6" />
      </button>
      
      <h2 className="text-xl font-bold text-slate-900">Scan QR Code</h2>
      <p className="text-slate-600 text-sm">Point camera at pallet QR code</p>
      
      {/* Camera Scanner Area */}
      <div className="relative mx-auto w-72 h-72 bg-slate-900 rounded-2xl overflow-hidden">
        <div id="qr-reader" ref={scannerRef} className="w-full h-full"></div>
        
        {!isScanning && !cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Camera className="w-16 h-16 text-white/50 mb-4" />
            <p className="text-white/70 text-sm">Camera not active</p>
          </div>
        )}
        
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            <CameraOff className="w-12 h-12 text-red-400 mb-3" />
            <p className="text-white text-sm text-center">{cameraError}</p>
            <p className="text-white/60 text-xs mt-2">Use manual entry below</p>
          </div>
        )}
        
        {/* Scanner overlay frame */}
        {isScanning && (
          <div className="absolute inset-4 border-2 border-cyan-400/50 rounded-xl pointer-events-none">
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-cyan-400 rounded-tl-lg"></div>
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-cyan-400 rounded-tr-lg"></div>
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-cyan-400 rounded-bl-lg"></div>
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-cyan-400 rounded-br-lg"></div>
          </div>
        )}
      </div>
      
      {/* Camera Controls */}
      <div className="flex gap-3">
        {!isScanning ? (
          <Button
            onClick={startScanner}
            className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 rounded-xl"
          >
            <Camera className="w-5 h-5 mr-2" />
            Start Camera
          </Button>
        ) : (
          <Button
            onClick={stopScanner}
            variant="outline"
            className="flex-1 h-12 rounded-xl"
          >
            <CameraOff className="w-5 h-5 mr-2" />
            Stop Camera
          </Button>
        )}
        
        <Button
          onClick={handleSimulateScan}
          variant="outline"
          className="h-12 px-4 rounded-xl"
          title="Simulate scan for demo"
        >
          <QrCode className="w-5 h-5" />
        </Button>
      </div>
      
      {/* Manual Entry */}
      <div className="pt-4 border-t border-slate-200">
        <p className="text-xs text-slate-500 mb-3">Or enter SSCC manually</p>
        <div className="flex gap-2">
          <Input
            placeholder="Enter SSCC (18 digits)"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            className="h-11 rounded-xl"
          />
          <Button onClick={handleManualSubmit} className="h-11 px-4 rounded-xl">
            Go
          </Button>
        </div>
      </div>
    </div>
  );
};

// Form Step Component
const FormStep = ({ onBack, onSubmit, formData, onInputChange, services, onServiceChange, isPending }) => (
  <div className="space-y-5">
    <button
      onClick={onBack}
      className="absolute top-4 left-4 p-2 text-slate-600 hover:text-slate-900"
    >
      <ArrowLeft className="w-6 h-6" />
    </button>
    
    <h2 className="text-xl font-bold text-slate-900 text-center">Activate Shipment</h2>
    
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">Scanned Data</div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-slate-500">SSCC:</span> <span className="font-mono font-semibold">{formData.sscc.slice(-8)}</span></div>
          <div><span className="text-slate-500">Pallet:</span> <span className="font-semibold">{formData.pallet_id}</span></div>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="destination" className="text-sm font-semibold">Destination</Label>
        <Input
          id="destination"
          name="destination"
          placeholder="e.g., Bergen Store"
          value={formData.destination}
          onChange={onInputChange}
          className="h-12 text-base rounded-xl"
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="origin" className="text-sm font-semibold">Origin</Label>
          <Input
            id="origin"
            name="origin"
            placeholder="Oslo Warehouse"
            value={formData.origin}
            onChange={onInputChange}
            className="h-12 text-base rounded-xl"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="receiver_id" className="text-sm font-semibold">Customer ID</Label>
          <Input
            id="receiver_id"
            name="receiver_id"
            placeholder="CUST001"
            value={formData.receiver_id}
            onChange={onInputChange}
            className="h-12 text-base rounded-xl"
            required
          />
        </div>
      </div>
      
      <div className="p-4 bg-slate-50 rounded-xl space-y-4">
        <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Services</div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Thermometer className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-sm">Temperature Monitoring</div>
              <div className="text-xs text-slate-500">Track temp continuously</div>
            </div>
          </div>
          <Switch
            checked={services.temperatureMonitoring}
            onCheckedChange={(checked) => onServiceChange('temperatureMonitoring', checked)}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="font-medium text-sm">Location Tracking</div>
              <div className="text-xs text-slate-500">GPS position updates</div>
            </div>
          </div>
          <Switch
            checked={services.locationTracking}
            onCheckedChange={(checked) => onServiceChange('locationTracking', checked)}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="font-medium text-sm">Full Traceability</div>
              <div className="text-xs text-slate-500">Complete audit trail</div>
            </div>
          </div>
          <Switch
            checked={services.fullTraceability}
            onCheckedChange={(checked) => onServiceChange('fullTraceability', checked)}
          />
        </div>
      </div>
      
      <Button
        type="submit"
        disabled={isPending}
        className="w-full h-14 text-lg bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 rounded-xl"
      >
        {isPending ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Activating...
          </>
        ) : (
          <>
            <CheckCircle className="w-5 h-5 mr-2" />
            Start Tracking
          </>
        )}
      </Button>
    </form>
  </div>
);

// Success Step Component
const SuccessStep = ({ selectedAction, createdShipment, scanResult, onNavigate, onReset }) => (
  <div className="space-y-6 text-center">
    <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto animate-bounce-once">
      <CheckCircle className="w-12 h-12 text-green-600" />
    </div>
    
    <div>
      <h2 className="text-2xl font-bold text-slate-900">Success!</h2>
      <p className="text-slate-600 mt-1">
        {selectedAction === 'new' && 'Shipment is now being tracked'}
        {selectedAction === 'status' && 'Shipment status retrieved'}
        {selectedAction === 'end' && 'Shipment has been completed'}
      </p>
    </div>
    
    {(createdShipment || scanResult) && (
      <div className="p-5 bg-slate-50 rounded-2xl text-left space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-slate-500 text-sm">SSCC</span>
          <code className="font-mono font-semibold bg-white px-3 py-1 rounded-lg">
            {createdShipment?.sscc || scanResult?.sscc}
          </code>
        </div>
        {createdShipment && (
          <>
            <div className="flex justify-between items-center">
              <span className="text-slate-500 text-sm">Route</span>
              <span className="font-semibold">{createdShipment.origin} → {createdShipment.destination}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500 text-sm">Status</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                {createdShipment.status?.replace('_', ' ')}
              </span>
            </div>
          </>
        )}
      </div>
    )}
    
    <div className="space-y-3">
      <Button
        onClick={onNavigate}
        className="w-full h-14 text-lg bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 rounded-xl"
      >
        View in Dashboard
      </Button>
      <Button
        onClick={onReset}
        variant="outline"
        className="w-full h-14 text-lg rounded-xl"
      >
        Scan Another
      </Button>
    </div>
  </div>
);

const MobileScanner = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [step, setStep] = useState('action');
  const [selectedAction, setSelectedAction] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [createdShipment, setCreatedShipment] = useState(null);
  
  const [formData, setFormData] = useState({
    sscc: '',
    pallet_id: '',
    device_id: '',
    receiver_id: '',
    origin: '',
    destination: '',
    expected_temp_min: -20.0,
    expected_temp_max: 8.0,
  });
  
  const [services, setServices] = useState({
    temperatureMonitoring: true,
    locationTracking: true,
    fullTraceability: false,
  });

  const { data: shipments = [] } = useQuery({
    queryKey: ['shipments'],
    queryFn: async () => {
      const response = await axios.get(`${API}/shipments`);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/shipments`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['shipments']);
      setCreatedShipment(data);
      setStep('success');
      toast.success('Shipment activated successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create shipment');
    },
  });

  const handleSelectAction = (action) => {
    setSelectedAction(action);
    setStep('scanning');
  };

  const handleScanComplete = (scannedData) => {
    setScanResult(scannedData);
    setFormData(prev => ({
      ...prev,
      sscc: scannedData.sscc,
      pallet_id: scannedData.pallet_id,
      device_id: scannedData.device_id,
    }));
    
    if (selectedAction === 'new') {
      setStep('form');
    } else if (selectedAction === 'status') {
      const found = shipments.find(s => s.sscc === scannedData.sscc);
      if (found) {
        setCreatedShipment(found);
      }
      setStep('success');
    } else if (selectedAction === 'end') {
      toast.success('Shipment ended successfully');
      setStep('success');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate({
      ...formData,
      expected_temp_min: parseFloat(formData.expected_temp_min),
      expected_temp_max: parseFloat(formData.expected_temp_max),
      services: services,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (service, checked) => {
    setServices(prev => ({ ...prev, [service]: checked }));
  };

  const resetWorkflow = () => {
    setStep('action');
    setSelectedAction(null);
    setScanResult(null);
    setCreatedShipment(null);
    setFormData({
      sscc: '',
      pallet_id: '',
      device_id: '',
      receiver_id: '',
      origin: '',
      destination: '',
      expected_temp_min: -20.0,
      expected_temp_max: 8.0,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-100 p-4" data-testid="mobile-scanner">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8 pt-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <QrCode className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">SmartPallet Scanner</h1>
          <p className="text-slate-600 text-sm">Mobile Tracking Interface</p>
        </div>
        
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`w-3 h-3 rounded-full transition-all ${step === 'action' ? 'bg-blue-600 scale-125' : 'bg-slate-300'}`}></div>
          <div className={`w-8 h-0.5 ${step !== 'action' ? 'bg-blue-600' : 'bg-slate-300'}`}></div>
          <div className={`w-3 h-3 rounded-full transition-all ${step === 'scanning' ? 'bg-blue-600 scale-125' : step !== 'action' ? 'bg-blue-600' : 'bg-slate-300'}`}></div>
          <div className={`w-8 h-0.5 ${step === 'form' || step === 'success' ? 'bg-blue-600' : 'bg-slate-300'}`}></div>
          <div className={`w-3 h-3 rounded-full transition-all ${step === 'form' || step === 'success' ? 'bg-blue-600 scale-125' : 'bg-slate-300'}`}></div>
        </div>
        
        <div className="bg-white rounded-3xl shadow-xl p-6 relative overflow-hidden">
          {step === 'action' && <ActionStep onSelectAction={handleSelectAction} />}
          {step === 'scanning' && (
            <ScanningStep
              onBack={() => { setStep('action'); setSelectedAction(null); }}
              onScanComplete={handleScanComplete}
              selectedAction={selectedAction}
            />
          )}
          {step === 'form' && (
            <FormStep
              onBack={() => setStep('scanning')}
              onSubmit={handleSubmit}
              formData={formData}
              onInputChange={handleInputChange}
              services={services}
              onServiceChange={handleServiceChange}
              isPending={createMutation.isPending}
            />
          )}
          {step === 'success' && (
            <SuccessStep
              selectedAction={selectedAction}
              createdShipment={createdShipment}
              scanResult={scanResult}
              onNavigate={() => navigate('/app/dashboard')}
              onReset={resetWorkflow}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileScanner;
